import React from 'react';
import {
  Shield, Users, Wallet, Truck, AlertTriangle, Activity, BookOpen,
} from 'lucide-react';
import AggTile from './globalSupervision/AggTile';
import SupervisionFilters from './globalSupervision/SupervisionFilters';
import SupervisionRow from './globalSupervision/SupervisionRow';
import TelemetryRejectsBlock from './globalSupervision/TelemetryRejectsBlock';
import useGlobalSupervisionData from './globalSupervision/useGlobalSupervisionData';
import { fmtNum } from './globalSupervision/format';

/**
 * GlobalSupervisionTab — admin-only « Super Vue » that aggregates every
 * apprentice across every cohort (plus indépendants). Read-only view.
 *
 * Refactored 29/05/2026 (code review) : the original 409-line monolith
 * (cyclomatic complexity 93) was decomposed into :
 *   • useGlobalSupervisionData (hook)  — fetch + state + memos
 *   • SupervisionFilters (component)   — search + filter pills + sort
 *   • SupervisionRow (component)       — one table row + expandable detail
 *   • DetailPanel (component)          — expanded telemetry block
 *   • AggTile (component)              — top KPI tile
 *   • format.js                        — shared formatters
 *
 * This file is now a thin orchestrator (~100 lines).
 */
const FILTERS = [
  { id: 'all',      label: 'Tous',              icon: Users },
  { id: 'playing',  label: 'Joue actuellement', icon: Activity },
  { id: 'unplayed', label: 'N\'a jamais joué',  icon: BookOpen },
  { id: 'alerts',   label: 'Alertes',           icon: AlertTriangle },
];

const SORTS = [
  { id: 'recent',     label: 'Activité récente' },
  { id: 'name',       label: 'Nom A→Z' },
  { id: 'cohort',     label: 'Cohorte' },
  { id: 'treasury',   label: 'Trésorerie' },
  { id: 'reputation', label: 'Réputation' },
  { id: 'days',       label: 'Jours joués' },
  { id: 'playtime',   label: 'Temps de jeu' },
];

export default function GlobalSupervisionTab({ token, hideTest = false }) {
  const {
    meta, loading, error,
    search, setSearch,
    filter, setFilter,
    sortBy, setSortBy,
    expandedKey, setExpandedKey,
    establishmentFilter, setEstablishmentFilter,
    aggregated, filteredSorted, establishmentOptions,
  } = useGlobalSupervisionData(token, { hideTest });

  return (
    <div className="space-y-5" data-testid="global-supervision-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2">
            <Shield className="w-5 h-5 text-purple-400" /> Super Vue Pédagogique
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Vue agrégée admin sur tous les apprenants (toutes cohortes + indépendants). Auto-refresh toutes les 90 s.
          </p>
        </div>
        <div className="text-xs text-slate-400">
          <span className="px-2 py-1 rounded bg-purple-500/10 text-purple-300 mr-2">
            {meta.masters_count} cohorte·s
          </span>
          <span className="px-2 py-1 rounded bg-money/10 text-money mr-2">
            {meta.playing_count}/{meta.total} jouent
          </span>
        </div>
      </div>

      {/* Aggregated tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <AggTile testId="agg-total"      label="Apprenants"          value={meta.total}              icon={Users}          color="text-slate-200" />
        <AggTile testId="agg-playing"    label="Joue actuellement"   value={`${aggregated.playing}`} icon={Activity}       color="text-money" />
        <AggTile testId="agg-fleet"      label="Camions cumulés"     value={fmtNum(aggregated.totalFleet)} icon={Truck}    color="text-sky-300" />
        <AggTile testId="agg-cash-alert" label="Trésorerie < 5k €"   value={aggregated.cashAlerts}   icon={Wallet}         color={aggregated.cashAlerts > 0 ? 'text-warn' : 'text-slate-400'} />
        <AggTile testId="agg-neg-equity" label="Capitaux négatifs"   value={aggregated.negEquity}    icon={AlertTriangle}  color={aggregated.negEquity > 0 ? 'text-danger' : 'text-slate-400'} />
      </div>

      <SupervisionFilters
        search={search} setSearch={setSearch}
        filter={filter} setFilter={setFilter}
        sortBy={sortBy} setSortBy={setSortBy}
        establishmentFilter={establishmentFilter} setEstablishmentFilter={setEstablishmentFilter}
        establishmentOptions={establishmentOptions}
        filters={FILTERS}
        sorts={SORTS}
      />

      {/* 17/07/2026 — snapshots refusés (clés inconnues / désactivées) */}
      <TelemetryRejectsBlock token={token} />

      {/* Loading / error / empty */}
      {loading && <p className="text-sm text-slate-500 italic py-6 text-center">Chargement…</p>}
      {error && <p className="text-sm text-danger py-6 text-center">{error}</p>}

      {!loading && !error && filteredSorted.length === 0 && (
        <div className="bg-ink-800 border border-ink-600 rounded-xl p-8 text-center" data-testid="global-empty">
          <Users className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-sm text-slate-400">Aucun apprenant ne correspond à ce filtre.</p>
        </div>
      )}

      {/* Table */}
      {!loading && !error && filteredSorted.length > 0 && (
        <div className="bg-ink-800 rounded-xl border border-ink-600 overflow-hidden" data-testid="global-table">
          <table className="w-full text-sm">
            <thead className="bg-ink-700/40 border-b border-ink-600">
              <tr>
                <th className="text-left px-4 py-2 text-[10px] font-semibold text-slate-500 uppercase">Apprenant</th>
                <th className="text-left px-4 py-2 text-[10px] font-semibold text-slate-500 uppercase">Cohorte</th>
                <th className="text-left px-4 py-2 text-[10px] font-semibold text-slate-500 uppercase">Société</th>
                <th className="text-right px-4 py-2 text-[10px] font-semibold text-slate-500 uppercase">Jours</th>
                <th className="text-right px-4 py-2 text-[10px] font-semibold text-slate-500 uppercase">Temps de jeu</th>
                <th className="text-right px-4 py-2 text-[10px] font-semibold text-slate-500 uppercase">Trésorerie</th>
                <th className="text-right px-4 py-2 text-[10px] font-semibold text-slate-500 uppercase">Capitaux</th>
                <th className="text-right px-4 py-2 text-[10px] font-semibold text-slate-500 uppercase">Flotte</th>
                <th className="text-right px-4 py-2 text-[10px] font-semibold text-slate-500 uppercase">Réputation</th>
                <th className="text-right px-4 py-2 text-[10px] font-semibold text-slate-500 uppercase"></th>
              </tr>
            </thead>
            <tbody>
              {filteredSorted.map(it => {
                const key = it.license?.license_key;
                return (
                  <SupervisionRow
                    key={key}
                    item={it}
                    token={token}
                    expanded={expandedKey === key}
                    onToggle={() => setExpandedKey(expandedKey === key ? null : key)}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
